import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError, downloadFile } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import OpticalSheet from "@/components/OpticalSheet";
import { ArrowLeft, ScanLine, Printer, Loader2, FileDown, UserMinus, Layers, Plus } from "lucide-react";

export default function OpticalForms() {
  const { examId } = useParams();
  const nav = useNavigate();
  const [exam, setExam] = useState(null);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [excluded, setExcluded] = useState([]);
  const [forms, setForms] = useState([]);
  const [scope, setScope] = useState("class");
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [sheets, setSheets] = useState(null); // loaded sheet data for print

  const load = () => api.get(`/exams/${examId}/optical-forms`).then((r) => setForms(r.data));
  useEffect(() => {
    api.get(`/exams/${examId}`).then((r) => setExam(r.data));
    api.get("/classes").then((r) => setClasses(r.data));
    api.get("/students").then((r) => setStudents(r.data)).catch(() => {});
    load();
    // eslint-disable-next-line
  }, [examId]);

  const toggleClass = (name) => setSelectedClasses((s) => s.includes(name) ? s.filter((x) => x !== name) : [...s, name]);
  const toggleExclude = (id) => setExcluded((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const eligibleStudents = useMemo(() => {
    let list = students;
    if (scope !== "school") list = students.filter((s) => selectedClasses.includes(`${s.grade}-${s.branch}`));
    return [...list].sort((a, b) => (`${a.grade}-${a.branch}`).localeCompare(`${b.grade}-${b.branch}`) || ((a.number || 0) - (b.number || 0)));
  }, [students, scope, selectedClasses]);

  const generate = async () => {
    setGenerating(true);
    try {
      if (scope !== "school" && selectedClasses.length === 0) { toast.error("En az bir sınıf seçin"); setGenerating(false); return; }
      const eligibleIds = new Set(eligibleStudents.map((s) => s.id));
      const exclude_ids = excluded.filter((id) => eligibleIds.has(id));
      const body = scope === "school" ? { scope: "school", exclude_ids } : { scope: "multi", classes: selectedClasses, exclude_ids };
      const { data } = await api.post(`/exams/${examId}/optical-forms`, body);
      load();
      const skippedMsg = data.skipped > 0 ? ` · ${data.skipped} öğrenci hariç tutuldu (hasta/izinli)` : "";
      if (data.created > 0) {
        toast.success(`${data.created} optik form oluşturuldu (toplam ${data.total} öğrenci)${skippedMsg}`);
      } else if (data.existing > 0) {
        toast.info(`Bu kapsamdaki ${data.existing} öğrenci için optik formlar zaten mevcut.${skippedMsg} Aşağıdan yazdırabilir veya PDF indirebilirsiniz.`);
      } else {
        toast.info("Form oluşturulacak öğrenci bulunamadı.");
      }
    } catch (e) { toast.error(apiError(e)); }
    finally { setGenerating(false); }
  };

  const downloadPdf = async (qs, name = "optik_formlar.pdf") => {
    try {
      await downloadFile(`/exams/${examId}/optical-forms.pdf?${qs}`, name);
      toast.success("PDF indirildi");
    } catch (e) { toast.error(apiError(e, "PDF oluşturulamadı")); }
  };

  const grouped = useMemo(() => {
    const g = {};
    forms.forEach((f) => { const k = `${f.grade}-${f.branch}`; (g[k] = g[k] || []).push(f); });
    return g;
  }, [forms]);

  const printForms = async (list) => {
    toast.info("Optik formlar hazırlanıyor...");
    try {
      const data = await Promise.all(list.map((f) => api.get(`/optical-forms/${f.id}`).then((r) => r.data)));
      setSheets(data);
      setTimeout(() => { window.print(); }, 600);
    } catch (e) { toast.error(apiError(e)); }
  };

  // --- İlave bölüm (ek ders) iş akışı ---
  const [addSel, setAddSel] = useState({});
  const [addCounts, setAddCounts] = useState({});
  const [addBusy, setAddBusy] = useState(false);
  const grades = useMemo(() => Object.keys(exam?.grade_configs || {}).sort(), [exam]);
  const gcKeys = useMemo(() => {
    const out = {};
    Object.entries(exam?.grade_configs || {}).forEach(([g, subs]) => { out[g] = new Set((subs || []).map((s) => s.key)); });
    return out;
  }, [exam]);
  const candidateSubjects = useMemo(() => {
    const base = exam?.subjects || [];
    if (!grades.length) return [];
    return base.filter((s) => grades.some((g) => !gcKeys[g]?.has(s.key)));
  }, [exam, grades, gcKeys]);
  const suppKeys = exam?.supplement_subjects || [];
  const setAddCount = (g, key, v) => setAddCounts((p) => ({ ...p, [g]: { ...(p[g] || {}), [key]: v } }));

  const addSubjects = async () => {
    const chosen = candidateSubjects.filter((s) => addSel[s.key]);
    if (!chosen.length) { toast.error("Eklenecek ders seçin"); return; }
    const counts = {};
    grades.forEach((g) => { counts[g] = {}; chosen.forEach((s) => { counts[g][s.key] = parseInt((addCounts[g] || {})[s.key]) || 0; }); });
    setAddBusy(true);
    try {
      const { data } = await api.patch(`/exams/${examId}/add-subjects`, { subjects: chosen.map((s) => ({ key: s.key, name: s.name, order: s.order })), counts });
      toast.success(`İlave dersler eklendi: ${(data.added || []).join(", ")}. Şimdi Cevap Anahtarı ekranından yeni derslerin anahtarlarını girin.`);
      const r = await api.get(`/exams/${examId}`); setExam(r.data);
      setAddSel({});
    } catch (e) { toast.error(apiError(e)); }
    finally { setAddBusy(false); }
  };

  const genSupplement = async () => {
    if (!suppKeys.length) { toast.error("Önce ilave ders ekleyin"); return; }
    if (scope !== "school" && selectedClasses.length === 0) { toast.error("Üstte en az bir sınıf seçin"); return; }
    const eligibleIds = new Set(eligibleStudents.map((s) => s.id));
    const exclude_ids = excluded.filter((id) => eligibleIds.has(id));
    const body = scope === "school" ? { scope: "school", subjects: suppKeys, exclude_ids } : { scope: "multi", classes: selectedClasses, subjects: suppKeys, exclude_ids };
    try {
      const { data } = await api.post(`/exams/${examId}/supplement-forms`, body);
      if (data.created > 0) toast.success(`${data.created} ilave optik form üretildi. Aşağıdan PDF indirip bastırın.`);
      else if (data.existing > 0) toast.info(`${data.existing} öğrenci için ilave form zaten mevcut. PDF indirebilirsiniz.`);
      else toast.info("İlave form üretilecek öğrenci bulunamadı.");
    } catch (e) { toast.error(apiError(e)); }
  };

  // Formları (idempotent) üretip hemen indirir — tek tıkla çalışır, "önce üret" hatası vermez
  const genAndDownloadSupplement = async (kind) => {
    if (!suppKeys.length) { toast.error("Önce ilave ders ekleyin"); return; }
    const isSchool = kind === "all";
    if (!isSchool && selectedClasses.length === 0) { toast.error("Önce yukarıdan en az bir sınıf seçin"); return; }
    const eligibleIds = new Set(eligibleStudents.map((s) => s.id));
    const exclude_ids = excluded.filter((id) => eligibleIds.has(id));
    const body = isSchool
      ? { scope: "school", subjects: suppKeys, exclude_ids }
      : { scope: "multi", classes: selectedClasses, subjects: suppKeys, exclude_ids };
    const qs = isSchool ? "scope=school" : `classes=${selectedClasses.join(",")}`;
    const name = isSchool ? `ilave_optik_${exam.name}.pdf` : "ilave_optik_secili.pdf";
    try {
      await api.post(`/exams/${examId}/supplement-forms`, body);
      await downloadFile(`/exams/${examId}/supplement-forms.pdf?${qs}`, name);
      toast.success("İlave optik PDF indirildi");
    } catch (e) { toast.error(apiError(e, "İlave PDF oluşturulamadı")); }
  };

  if (!exam) return <div className="text-muted-foreground">Yükleniyor...</div>;

  return (
    <div>
      {sheets && (
        <div className="print-area">
          {sheets.map((d) => <OpticalSheet key={d.form.id} data={d} />)}
          <div className="no-print fixed top-4 left-1/2 -translate-x-1/2 z-50 flex gap-2 bg-card border border-border rounded-full px-4 py-2 shadow-lg">
            <Button size="sm" onClick={() => window.print()}><Printer className="h-4 w-4 mr-2" />Yazdır / PDF</Button>
            <Button size="sm" variant="outline" onClick={() => setSheets(null)}>Kapat</Button>
          </div>
        </div>
      )}

      <div className={sheets ? "no-print" : ""}>
        <Button variant="ghost" size="sm" onClick={() => nav("/sinavlar")} className="mb-2 -ml-2"><ArrowLeft className="h-4 w-4 mr-1" />Sınavlar</Button>
        <PageHeader title={`Optik Formlar — ${exam.name}`} desc="Öğrenciler için kişiselleştirilmiş A4 optik formlar oluşturun ve yazdırın." />

        <div className="grid lg:grid-cols-3 gap-6">
          <Card className="p-5 bg-card border-border space-y-4">
            <h3 className="font-semibold">Optik Form Oluştur</h3>
            <div className="space-y-2">
              <Label>Kapsam</Label>
              <Select value={scope} onValueChange={setScope}>
                <SelectTrigger data-testid="optic-scope"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="class">Seçili Sınıf(lar)</SelectItem>
                  <SelectItem value="school">Tüm Okul</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {scope !== "school" && (
              <div className="space-y-2">
                <Label>Sınıflar</Label>
                <label className="flex items-center gap-2 p-2 rounded-md bg-muted cursor-pointer" data-testid="optic-select-all">
                  <Checkbox
                    checked={selectedClasses.length === classes.length && classes.length > 0}
                    onCheckedChange={(v) => setSelectedClasses(v ? classes.map((c) => c.name) : [])}
                  />
                  <span className="text-sm font-semibold">Tümünü Seç</span>
                </label>
                <div className="space-y-1.5 max-h-56 overflow-y-auto ktt-scroll">
                  {classes.map((c) => (
                    <label key={c.id} className="flex items-center gap-2 p-2 rounded-md hover:bg-muted cursor-pointer" data-testid={`optic-class-${c.name}`}>
                      <Checkbox checked={selectedClasses.includes(c.name)} onCheckedChange={() => toggleClass(c.name)} />
                      <span className="text-sm">{c.name}</span>
                      <span className="text-xs text-muted-foreground ml-auto">{c.student_count} öğrenci</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            {(scope === "school" || selectedClasses.length > 0) && eligibleStudents.length > 0 && (
              <div className="space-y-2 pt-1">
                <Label className="flex items-center gap-1.5"><UserMinus className="h-4 w-4" />Hasta / İzinli Öğrenciler</Label>
                <p className="text-xs text-muted-foreground">İşaretlediğiniz öğrenciler için optik form <strong>oluşturulmaz</strong>.</p>
                <div className="space-y-1 max-h-56 overflow-y-auto ktt-scroll border border-border rounded-md p-2" data-testid="optic-exclude-list">
                  {eligibleStudents.map((s) => (
                    <label key={s.id} className="flex items-center gap-2 p-1.5 rounded-md hover:bg-muted cursor-pointer" data-testid={`optic-exclude-${s.number}`}>
                      <Checkbox checked={excluded.includes(s.id)} onCheckedChange={() => toggleExclude(s.id)} />
                      <span className="text-sm truncate">{s.number} · {s.first_name} {s.last_name}</span>
                      <span className="text-xs text-muted-foreground ml-auto shrink-0">{s.grade}-{s.branch}</span>
                    </label>
                  ))}
                </div>
                {excluded.filter((id) => eligibleStudents.some((s) => s.id === id)).length > 0 && (
                  <p className="text-xs font-semibold text-amber-600 dark:text-amber-400" data-testid="optic-exclude-count">
                    {excluded.filter((id) => eligibleStudents.some((s) => s.id === id)).length} öğrenci hariç tutulacak
                  </p>
                )}
              </div>
            )}
            <Button className="w-full" onClick={generate} disabled={generating} data-testid="optic-generate">
              {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ScanLine className="h-4 w-4 mr-2" />}Formları Oluştur
            </Button>
          </Card>

          <Card className="lg:col-span-2 p-5 bg-card border-border">
            <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
              <h3 className="font-semibold">Oluşturulan Formlar ({forms.length})</h3>
              {forms.length > 0 && (
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => downloadPdf("scope=school", `optik_${exam.name}.pdf`)} data-testid="pdf-all"><FileDown className="h-4 w-4 mr-2" />PDF İndir</Button>
                  <Button size="sm" onClick={() => printForms(forms)} data-testid="print-all"><Printer className="h-4 w-4 mr-2" />Tümünü Yazdır</Button>
                </div>
              )}
            </div>
            {forms.length === 0 ? (
              <EmptyState icon={ScanLine} title="Henüz optik form yok" desc="Soldan kapsam seçip form oluşturun." />
            ) : (
              <div className="space-y-4">
                {Object.entries(grouped).map(([cls, list]) => (
                  <div key={cls}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-semibold text-sm">{cls} <span className="text-muted-foreground font-normal">· {list.length} form</span></div>
                      <div className="flex gap-1.5">
                        <Button size="sm" variant="outline" onClick={() => downloadPdf(`classes=${cls}`, `optik_${cls}.pdf`)} data-testid={`pdf-class-${cls}`}><FileDown className="h-3.5 w-3.5 mr-1.5" />PDF</Button>
                        <Button size="sm" variant="outline" onClick={() => printForms(list)} data-testid={`print-class-${cls}`}><Printer className="h-3.5 w-3.5 mr-1.5" />Yazdır</Button>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {list.map((f) => (
                        <button key={f.id} onClick={() => printForms([f])} className="text-xs px-2.5 py-1 rounded-md border border-border hover:border-primary hover:text-primary" data-testid={`optic-form-${f.number}`}>
                          {f.number} · {f.student_name}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {grades.length > 0 && (candidateSubjects.length > 0 || suppKeys.length > 0) && (
          <Card className="p-5 bg-card border-border mt-6 space-y-4" data-testid="supplement-card">
            <div className="flex items-center gap-2">
              <Layers className="h-5 w-5 text-primary" />
              <h3 className="font-semibold">İlave Bölüm (Ek Ders) — 6 Derse Tamamla</h3>
            </div>
            <p className="text-sm text-muted-foreground">
              Bu sınavda zaten okunan dersler korunur. Yeni dersleri ekleyin, <strong>ayrı</strong> bir optik form bastırın;
              okuttuğunuzda yeni dersler mevcut öğrenci sonuçlarına <strong>eklenir</strong> ve netice 6 ders üzerinden hesaplanır.
            </p>

            {candidateSubjects.length > 0 && (
              <div className="space-y-3 border border-border rounded-lg p-4">
                <Label className="font-semibold">1) Eklenecek dersleri ve sınıf başına soru sayısını girin</Label>
                <div className="flex flex-wrap gap-3">
                  {candidateSubjects.map((s) => (
                    <label key={s.key} className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-border cursor-pointer hover:border-primary" data-testid={`supp-sel-${s.key}`}>
                      <Checkbox checked={!!addSel[s.key]} onCheckedChange={(v) => setAddSel((p) => ({ ...p, [s.key]: !!v }))} />
                      <span className="text-sm font-medium">{s.name}</span>
                    </label>
                  ))}
                </div>
                {candidateSubjects.some((s) => addSel[s.key]) && (
                  <div className="overflow-x-auto">
                    <table className="text-sm border-collapse">
                      <thead>
                        <tr className="text-left text-muted-foreground">
                          <th className="p-2">Sınıf</th>
                          {candidateSubjects.filter((s) => addSel[s.key]).map((s) => <th key={s.key} className="p-2 text-center">{s.name}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {grades.map((g) => (
                          <tr key={g} className="border-t border-border">
                            <td className="p-2 font-medium">{g}. Sınıf</td>
                            {candidateSubjects.filter((s) => addSel[s.key]).map((s) => (
                              <td key={s.key} className="p-2 text-center">
                                <Input type="number" min="0" className="w-20" value={(addCounts[g] || {})[s.key] ?? ""} placeholder="0"
                                  onChange={(e) => setAddCount(g, s.key, e.target.value)} data-testid={`supp-count-${g}-${s.key}`} />
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <Button onClick={addSubjects} disabled={addBusy} data-testid="supp-add-btn">
                  {addBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}Dersleri Ekle
                </Button>
              </div>
            )}

            {suppKeys.length > 0 && (
              <div className="space-y-3 border border-border rounded-lg p-4">
                <Label className="font-semibold">2) İlave optik formu üretin ve bastırın</Label>
                <p className="text-xs text-muted-foreground">
                  İlave dersler: <strong>{suppKeys.join(", ")}</strong>. Üstteki <strong>kapsam/sınıf seçimini</strong> kullanır.
                  Cevap anahtarlarını girdikten sonra bu formları okutun (sonuçlara otomatik eklenir).
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" onClick={genSupplement} data-testid="supp-gen-btn"><ScanLine className="h-4 w-4 mr-2" />İlave Form Üret</Button>
                  <Button variant="outline" onClick={() => genAndDownloadSupplement("all")} data-testid="supp-pdf-all"><FileDown className="h-4 w-4 mr-2" />İlave Form PDF (Tümü)</Button>
                  {scope !== "school" && selectedClasses.length > 0 && (
                    <Button variant="outline" onClick={() => genAndDownloadSupplement("selected")} data-testid="supp-pdf-class"><FileDown className="h-4 w-4 mr-2" />İlave Form PDF (Seçili Sınıflar)</Button>
                  )}
                </div>
              </div>
            )}
          </Card>
        )}
      </div>
    </div>
  );
}
